import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '@/shared/lib/utils';

const buttonVariants = cva(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
    {
        variants: {
            variant: {
                default: 'border-accent/50 bg-accent/15 text-foreground hover:bg-accent/20',
                secondary: 'border-border bg-panel text-foreground hover:bg-card',
                ghost: 'border-transparent bg-transparent text-muted hover:text-foreground',
                outline: 'border-border bg-transparent text-foreground hover:bg-card',
                destructive: 'border-transparent bg-red-500 text-white hover:bg-red-500/90',
                link: 'border-transparent bg-transparent px-0 py-0 text-accent underline-offset-4 hover:underline',
            },
            size: {
                default: 'h-10 px-4 py-2',
                sm: 'h-9 px-3',
                lg: 'h-11 px-8',
                icon: 'h-10 w-10 p-0',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

type ButtonVariant = 'default' | 'secondary' | 'ghost' | 'outline' | 'destructive' | 'link';
type ButtonSize = 'default' | 'sm' | 'lg' | 'icon';

export interface ButtonProps extends Omit<React.ComponentPropsWithoutRef<'button'>, 'size'> {
    asChild?: boolean;
    variant?: ButtonVariant;
    size?: ButtonSize;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button({ className, variant, size, asChild = false, type = 'button', ...props }, ref) {
    const Comp = asChild ? Slot : 'button';

    return (
        <Comp
            ref={ref}
            type={asChild ? undefined : type}
            className={cn(buttonVariants({ variant, size, className }))}
            {...props}
        />
    );
});

export { buttonVariants };